import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { AnimalsCondition, AnimalsConditionsParams } from 'models/AnimalsCondition';
import { AnimalsDisposition, AnimalsDispositionsParams } from 'models/AnimalsDisposition';
import { AnimalsOwner, AnimalsOwnersParams } from 'models/AnimalsOwner';
import { ApiMultipleResponse } from 'models/ApiModels';
import { ConfigType } from 'models/ConfigType';
import { ReduxState } from 'redux/store';

const baseUrl = process.env.REACT_APP_ANIMALS_BASE_URL;
const functionsKey = process.env.REACT_APP_ANIMALS_HOST_KEY;

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token && functionsKey) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

export const animalsApi = createApi({
  reducerPath: 'animalsApi',
  baseQuery,
  endpoints: (builder) => ({
    getOwners: builder.query<ApiMultipleResponse<AnimalsOwner>, AnimalsOwnersParams>({
      query: (params) => ({
        url: '/owners',
        params,
        responseHandler: (response): Promise<ApiMultipleResponse<AnimalsOwner> | string> => (response.status >= 300 ? response.text() : response.json())
      })
    }),
    getConditions: builder.query<ApiMultipleResponse<AnimalsCondition>, AnimalsConditionsParams>({
      query: (params) => ({
        url: '/conditions',
        params,
        responseHandler: (response): Promise<ApiMultipleResponse<AnimalsCondition> | string> => (response.status >= 300 ? response.text() : response.json())
      })
    }),
    getDispositions: builder.query<ApiMultipleResponse<AnimalsDisposition>, AnimalsDispositionsParams>({
      query: (params) => ({
        url: '/dispositions',
        params,
        responseHandler: (response): Promise<ApiMultipleResponse<AnimalsDisposition> | string> => (response.status >= 300 ? response.text() : response.json())
      })
    }),
    /**
     * Lists the D365 configuration types (`MDSi_ConfigTypes`) for one D365 company. This is the only
     * source of Default Config Type options in beaker. `dataAreaId` is required: Config Types are per company.
     */
    getConfigTypes: builder.query<ApiMultipleResponse<ConfigType>, { dataAreaId: string }>({
      query: (params) => ({
        url: '/configTypes',
        params,
        responseHandler: (response): Promise<ApiMultipleResponse<ConfigType> | string> => (response.status >= 300 ? response.text() : response.json())
      })
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetOwnersQuery, useGetConditionsQuery, useGetDispositionsQuery, useGetConfigTypesQuery, useGetVersionQuery, useGetApiNameQuery } = animalsApi;
